(() => {
  'use strict';

  if (window.__wheelDockAcceptanceLoaded) return;
  window.__wheelDockAcceptanceLoaded = true;

  const ROOT =
    window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root
      : '/';

  const ATTR = Object.freeze({
    policiesAccepted: 'WheelDock - Policies Accepted',
    acceptedAt: 'WheelDock - Accepted At',
    timezone: 'WheelDock - Acceptance Time Zone',
    policyVersion: 'WheelDock - Policy Version',

    stockRequired: 'WheelDock - Stock Bike Ack Required',
    stockAccepted: 'WheelDock - Stock Bike Ack Accepted',
    stockVersion: 'WheelDock - Stock Bike Ack Version',

    cartSignature: 'WheelDock - Accepted Cart'
  });

  const COMPONENT_SELECTOR = '[data-wd-acceptance]';
  const POLICY_SELECTOR = '[data-wd-policy-checkbox]';
  const STOCK_SELECTOR = '[data-wd-stock-checkbox]';
  const MESSAGE_SELECTOR = '[data-wd-acceptance-message]';

  const CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href="/checkout"]',
    'a[href^="/checkout?"]'
  ].join(',');

  let busy = false;
  let bypassCheckoutGuard = false;
  let lastValidState = false;
  let refreshTimer = null;
  let invalidateTimer = null;

  /* -------------------------------------------------
     Elements / state
  ------------------------------------------------- */

  function getComponents() {
    return Array.from(
      document.querySelectorAll(COMPONENT_SELECTOR)
    );
  }

  function getPolicyCheckboxes() {
    return Array.from(
      document.querySelectorAll(POLICY_SELECTOR)
    );
  }

  function getStockCheckboxes() {
    return Array.from(
      document.querySelectorAll(STOCK_SELECTOR)
    );
  }

  function getCheckoutControls() {
    return Array.from(
      document.querySelectorAll(CHECKOUT_SELECTOR)
    );
  }

  function stockAcknowledgmentRequired() {
    return getComponents().some(component => {
      return component.dataset.stockRequired === 'true';
    });
  }

  function getPolicyVersion() {
    const component =
      document.querySelector(COMPONENT_SELECTOR);

    return component
      ? component.dataset.policyVersion
      : '';
  }

  function getStockVersion() {
    const component =
      document.querySelector(COMPONENT_SELECTOR);

    return component
      ? component.dataset.stockVersion
      : '';
  }

  function policyChecked() {
    const boxes = getPolicyCheckboxes();

    return (
      boxes.length > 0 &&
      boxes.some(box => box.checked)
    );
  }

  function stockChecked() {
    if (!stockAcknowledgmentRequired()) {
      return true;
    }

    const boxes = getStockCheckboxes();

    return (
      boxes.length > 0 &&
      boxes.some(box => box.checked)
    );
  }

  function allRequiredBoxesChecked() {
    return policyChecked() && stockChecked();
  }

  function setPolicyCheckboxes(checked) {
    getPolicyCheckboxes().forEach(box => {
      box.checked = checked;
    });
  }

  function setStockCheckboxes(checked) {
    getStockCheckboxes().forEach(box => {
      box.checked = checked;
    });
  }

  function setCheckboxesDisabled(disabled) {
    [
      ...getPolicyCheckboxes(),
      ...getStockCheckboxes()
    ].forEach(box => {
      box.disabled = disabled;
    });
  }

  function showMessage(message = '', type = '') {
    document
      .querySelectorAll(MESSAGE_SELECTOR)
      .forEach(element => {
        element.textContent = message;

        if (type) {
          element.dataset.type = type;
        } else {
          delete element.dataset.type;
        }
      });
  }

  function setCheckoutEnabled(enabled) {
    lastValidState = enabled;

    getCheckoutControls().forEach(control => {
      control.classList.toggle(
        'wd-checkout-disabled',
        !enabled
      );

      control.setAttribute(
        'aria-disabled',
        enabled ? 'false' : 'true'
      );

      if ('disabled' in control) {
        control.disabled = !enabled;
      }
    });
  }

  /* -------------------------------------------------
     Shopify cart
  ------------------------------------------------- */

  async function getCart() {
    const response = await fetch(
      ROOT + 'cart.js',
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to load Shopify cart.'
      );
    }

    return await response.json();
  }

  async function updateCartAttributes(attributes) {
    const response = await fetch(
      ROOT + 'cart/update.js',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          attributes
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to save checkout acknowledgment.'
      );
    }

    return await response.json();
  }

  /* -------------------------------------------------
     Cart snapshot
  ------------------------------------------------- */

  function createCartSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return '';
    }

    return cart.items
      .map(item => {
        return `${item.key}:${item.quantity}`;
      })
      .sort()
      .join('|');
  }

  /* -------------------------------------------------
     Verify saved acceptance
  ------------------------------------------------- */

  function acceptanceIsValid(cart) {
    if (!cart) return false;

    const attributes = cart.attributes || {};

    const currentSignature =
      createCartSignature(cart);

    const stockRequired =
      stockAcknowledgmentRequired();

    const policyValid =
      attributes[ATTR.policiesAccepted] === 'Yes' &&
      attributes[ATTR.policyVersion] ===
        getPolicyVersion();

    const stockValid =
      !stockRequired ||
      (
        attributes[ATTR.stockRequired] === 'Yes' &&
        attributes[ATTR.stockAccepted] === 'Yes' &&
        attributes[ATTR.stockVersion] ===
          getStockVersion()
      );

    const cartValid =
      attributes[ATTR.cartSignature] ===
        currentSignature;

    return (
      policyValid &&
      stockValid &&
      cartValid
    );
  }

  /* -------------------------------------------------
     Save completed acknowledgments
  ------------------------------------------------- */

  async function saveAcceptance() {
    if (busy) return;

    if (!allRequiredBoxesChecked()) {
      setCheckoutEnabled(false);
      return;
    }

    busy = true;

    setCheckboxesDisabled(true);
    setCheckoutEnabled(false);

    showMessage(
      'Saving your acknowledgment...',
      ''
    );

    try {
      const cart = await getCart();

      const timestamp =
        new Date().toISOString();

      let timezone = '';

      try {
        timezone =
          Intl.DateTimeFormat()
            .resolvedOptions()
            .timeZone || '';
      } catch (error) {
        timezone = '';
      }

      const stockRequired =
        stockAcknowledgmentRequired();

      await updateCartAttributes({
        [ATTR.policiesAccepted]: 'Yes',

        [ATTR.acceptedAt]:
          timestamp,

        [ATTR.timezone]:
          timezone,

        [ATTR.policyVersion]:
          getPolicyVersion(),

        [ATTR.stockRequired]:
          stockRequired
            ? 'Yes'
            : 'No',

        [ATTR.stockAccepted]:
          stockRequired
            ? 'Yes'
            : 'Not Required',

        [ATTR.stockVersion]:
          stockRequired
            ? getStockVersion()
            : '',

        [ATTR.cartSignature]:
          createCartSignature(cart)
      });

      setPolicyCheckboxes(true);

      if (stockRequired) {
        setStockCheckboxes(true);
      }

      lastValidState = true;

      showMessage(
        'Acknowledgment saved.',
        'success'
      );
    } catch (error) {
      console.error(
        '[WheelDock Acceptance]',
        error
      );

      setCheckoutEnabled(false);
      lastValidState = false;

      showMessage(
        'We could not save your acknowledgment. Please try again.',
        'error'
      );
    } finally {
      busy = false;

      setCheckboxesDisabled(false);
      setCheckoutEnabled(lastValidState);
    }
  }

  /* -------------------------------------------------
     Clear saved acceptance
  ------------------------------------------------- */

  async function clearSavedAcceptance() {
    lastValidState = false;
    setCheckoutEnabled(false);

    try {
      await updateCartAttributes({
        [ATTR.policiesAccepted]: 'No',

        [ATTR.acceptedAt]:
          '',

        [ATTR.timezone]:
          '',

        [ATTR.policyVersion]:
          '',

        [ATTR.stockRequired]:
          stockAcknowledgmentRequired()
            ? 'Yes'
            : 'No',

        [ATTR.stockAccepted]:
          'No',

        [ATTR.stockVersion]:
          '',

        [ATTR.cartSignature]:
          ''
      });
    } catch (error) {
      console.error(
        '[WheelDock Acceptance]',
        error
      );
    }
  }

  async function clearAcceptance({
    silent = false
  } = {}) {
    setPolicyCheckboxes(false);
    setStockCheckboxes(false);

    setCheckoutEnabled(false);
    lastValidState = false;

    if (!silent) {
      showMessage(
        'Please accept all required acknowledgments before checkout.',
        ''
      );
    }

    await clearSavedAcceptance();
  }

  /* -------------------------------------------------
     Restore existing valid acceptance
  ------------------------------------------------- */

  async function refreshAcceptanceState() {
    if (!getComponents().length) return;

    try {
      const cart = await getCart();

      const valid =
        acceptanceIsValid(cart);

      setPolicyCheckboxes(valid);

      if (stockAcknowledgmentRequired()) {
        setStockCheckboxes(valid);
      }

      setCheckoutEnabled(valid);

      if (valid) {
        showMessage(
          'Acknowledgment saved.',
          'success'
        );
      } else {
        showMessage('', '');
      }

      if (
        cart.attributes &&
        cart.attributes[
          ATTR.policiesAccepted
        ] === 'Yes' &&
        !valid
      ) {
        await clearSavedAcceptance();
      }
    } catch (error) {
      console.error(
        '[WheelDock Acceptance]',
        error
      );

      setCheckoutEnabled(false);
    }
  }

  /* -------------------------------------------------
     Checkbox behavior

     IMPORTANT:
     Either checkbox can be selected first.
     We never uncheck the other box simply because
     the customer hasn't completed both yet.
  ------------------------------------------------- */

  document.addEventListener(
    'change',
    async event => {
      const policyBox =
        event.target.closest(
          POLICY_SELECTOR
        );

      const stockBox =
        event.target.closest(
          STOCK_SELECTOR
        );

      if (!policyBox && !stockBox) {
        return;
      }

      /*
       * Synchronize duplicate copies of the same
       * checkbox between cart drawer / cart page.
       */

      if (policyBox) {
        setPolicyCheckboxes(
          policyBox.checked
        );
      }

      if (stockBox) {
        setStockCheckboxes(
          stockBox.checked
        );
      }

      const policiesAccepted =
        policyChecked();

      const stockAccepted =
        stockChecked();

      /*
       * Everything required has been checked.
       */

      if (
        policiesAccepted &&
        stockAccepted
      ) {
        await saveAcceptance();
        return;
      }

      /*
       * Customer still needs another acknowledgment.
       *
       * Keep whatever they already checked checked.
       * Only keep checkout disabled.
       */

      setCheckoutEnabled(false);
      lastValidState = false;

      if (
        stockAcknowledgmentRequired()
      ) {
        if (
          policiesAccepted &&
          !stockAccepted
        ) {
          showMessage(
            'One more acknowledgment is required below before checkout.',
            ''
          );
        } else if (
          !policiesAccepted &&
          stockAccepted
        ) {
          showMessage(
            'One more acknowledgment is required above before checkout.',
            ''
          );
        } else {
          showMessage(
            'Please complete both acknowledgments before checkout.',
            ''
          );
        }
      } else {
        showMessage(
          'Please accept the policies before checkout.',
          ''
        );
      }

      /*
       * Make sure an old saved acceptance can no
       * longer authorize checkout, but DON'T alter
       * the customer's currently checked boxes.
       */

      await clearSavedAcceptance();
    }
  );

  /* -------------------------------------------------
     Cart changed after acceptance
  ------------------------------------------------- */

  function invalidateBecauseCartChanged() {
    if (!lastValidState) return;

    setPolicyCheckboxes(false);
    setStockCheckboxes(false);

    setCheckoutEnabled(false);

    showMessage(
      'Your cart changed. Please review and accept the acknowledgments again.',
      ''
    );

    clearTimeout(invalidateTimer);

    invalidateTimer = setTimeout(() => {
      clearSavedAcceptance();
    }, 700);
  }

  document.addEventListener(
    'change',
    event => {
      const target = event.target;

      if (
        target.matches(
          'input[name^="updates["]'
        ) ||
        target.matches(
          '.quantity__input'
        ) ||
        target.matches(
          'select[name^="updates["]'
        )
      ) {
        invalidateBecauseCartChanged();
      }
    }
  );

  document.addEventListener(
    'click',
    event => {
      const cartChangeControl =
        event.target.closest(
          [
            'cart-remove-button',
            '.cart-remove-button',
            '[data-cart-remove]',
            '[data-remove-item]',
            '.quantity__button',
            '.quantity-button',
            '[name="plus"]',
            '[name="minus"]'
          ].join(',')
        );

      if (cartChangeControl) {
        invalidateBecauseCartChanged();
      }
    }
  );

  /* -------------------------------------------------
     Final checkout verification
  ------------------------------------------------- */

  async function attemptCheckout(trigger) {
    if (busy) return;

    busy = true;

    setCheckoutEnabled(false);

    showMessage(
      'Verifying your acknowledgment...',
      ''
    );

    try {
      const cart = await getCart();

      if (!acceptanceIsValid(cart)) {
        showMessage(
          'Please review and accept all required acknowledgments before checkout.',
          ''
        );

        await clearSavedAcceptance();

        return;
      }

      bypassCheckoutGuard = true;

      let destination =
        ROOT + 'checkout';

      if (
        trigger &&
        trigger.tagName === 'A' &&
        trigger.href
      ) {
        destination =
          trigger.href;
      }

      window.location.assign(
        destination
      );
    } catch (error) {
      console.error(
        '[WheelDock Acceptance]',
        error
      );

      showMessage(
        'We could not verify your acknowledgment. Please try again.',
        'error'
      );
    } finally {
      busy = false;

      if (!bypassCheckoutGuard) {
        setCheckoutEnabled(false);
      }
    }
  }

  /*
   * Capture checkout clicks before the theme.
   */

  document.addEventListener(
    'click',
    event => {
      const checkout =
        event.target.closest(
          CHECKOUT_SELECTOR
        );

      if (
        !checkout ||
        bypassCheckoutGuard
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      attemptCheckout(checkout);
    },
    true
  );

  /*
   * Protect keyboard / form submission too.
   */

  document.addEventListener(
    'submit',
    event => {
      if (bypassCheckoutGuard) {
        return;
      }

      const submitter =
        event.submitter;

      if (
        !submitter ||
        !submitter.matches(
          'button[name="checkout"], input[name="checkout"]'
        )
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      attemptCheckout(submitter);
    },
    true
  );

  /* -------------------------------------------------
     Cart drawer AJAX rebuilding
  ------------------------------------------------- */

  const observer =
    new MutationObserver(mutations => {
      let relevantChange = false;

      for (const mutation of mutations) {
        for (
          const node of mutation.addedNodes
        ) {
          if (node.nodeType !== 1) {
            continue;
          }

          if (
            node.matches?.(
              COMPONENT_SELECTOR
            ) ||
            node.querySelector?.(
              COMPONENT_SELECTOR
            ) ||
            node.matches?.(
              CHECKOUT_SELECTOR
            ) ||
            node.querySelector?.(
              CHECKOUT_SELECTOR
            )
          ) {
            relevantChange = true;
            break;
          }
        }

        if (relevantChange) {
          break;
        }
      }

      if (!relevantChange) {
        return;
      }

      /*
       * If acceptance was fully saved, restore the
       * visual state immediately after the drawer
       * gets rebuilt.
       */

      if (lastValidState) {
        setPolicyCheckboxes(true);

        if (
          stockAcknowledgmentRequired()
        ) {
          setStockCheckboxes(true);
        }
      }

      setCheckoutEnabled(
        lastValidState
      );

      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        refreshAcceptanceState();
      }, 150);
    });

  /* -------------------------------------------------
     Initialize
  ------------------------------------------------- */

  function initialize() {
    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );

    if (getComponents().length) {
      setCheckoutEnabled(false);

      refreshAcceptanceState();
    }
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }
})();